import {
    all,
    call,
    cancel,
    fork,
    put,
    race,
    select,
    take,
    takeEvery
} from 'redux-saga/effects';
import axios, { AxiosPromise, AxiosResponse } from 'axios';
import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import moment from 'moment';
import fileDownload from 'js-file-download';

// Types
import { SagaIterator } from 'redux-saga';

// @ts-ignore
import { resetSelectedTableCells } from 'services/selectedTableCells/actions';

import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from 'components/DatePagination/selectors';

// Global Selectors
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';

// Constants
import { TIMESHEET_UNAPPROVE_MODAL } from 'scenes/Time/components/TimesheetUnapproveModal';
import { makeSelectQueryStringBooleanValue } from 'workspaces/react-router-selectors';
import {
    TIMESHEETS_LOAD_REQUESTED,
    TIMESHEETS_LOAD_CANCELED,
    TIMESHEET_APPROVE_REQUESTED,
    TIMESHEET_UNAPPROVE_REQUESTED,
    TIMESHEET_DELETE_REQUESTED,
    TIMESHEET_SUBMIT_REQUESTED,
    TIMESHEET_MULTIPLE_SUBMIT_REQUESTED,
    DATAPAY_HOLIDAY_DAYS_EXPORT_REQUESTED
} from './constants';

// Actions
// @ts-ignore
import { setResult } from '../results/actions';
// @ts-ignore
import { setCount } from '../counts/actions';
// @ts-ignore
import { enqueueSnackbar } from '../notifications/actions';
// @ts-ignore
import { updateQueryString } from '../router/actions';
import {
    succeededLoadTimesheets,
    failedLoadTimesheets,
    succeededApproveTimesheet,
    failedApproveTimesheet,
    succeededSubmitTimesheet,
    failedSubmitTimesheet,
    setTimesheets,
    succeededDeleteTimesheet,
    failedDeleteTimesheet,
    succeededSubmitMultipleTimesheets,
    failedSubmitMultipleTimesheets,
    succeededExportDatapayHolidayDays,
    failedExportDatapayHolidayDays,
    succeededUnapproveTimesheet,
    failedUnapproveTimesheet
} from './actions';

// Selectors
// @ts-ignore
import { idTokenSelector } from '../auth/selectors';
// @ts-ignore
import { selectLoadTimesheetsParams } from './selectors';

// Utils
// @ts-ignore
import axiosPoll from '../../utils/axiosPoll';

// Types
import {
    RequestApproveTimesheetAction,
    RequestDeleteTimesheetAction,
    RequestSubmitTimesheetAction,
    RequestSubmitMultipleTimesheetsAction,
    RequestUnapproveTimesheetAction
} from './types';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* loadTimesheetsSucceeded(
    response: Record<string, any>
): SagaIterator {
    const { data } = response;
    const count = get(response, 'headers.x-total-count');

    const timesheetsKeyedById = keyBy(data, 'id');
    const timesheetIds = data.map(
        (value: Record<string, any>): number => value.id
    );

    const createTimesheetModalIsOpen = yield select(
        makeSelectQueryStringBooleanValue('createTimesheetModal')
    );

    // Set Timesheet Entries
    yield put(setTimesheets(timesheetsKeyedById));

    /*
     Timesheets are loaded for certain weeks when create timesheet modal is open, as they are used to validate
     whether there are timesheets for a specific date and group, but at the same time, we don't want to display
     them in the main timesheet table, as the filtering happens on the GET request
     */
    if (!createTimesheetModalIsOpen) {
        // Set Count
        yield put(setCount('TIMESHEETS_LOAD', count));

        // Set Result
        yield put(setResult('TIMESHEETS_LOAD', timesheetIds));
    }

    yield put(succeededLoadTimesheets(response));
}

export function* loadTimesheetsFailed(error: Error): SagaIterator {
    yield put(failedLoadTimesheets(error));
}

export function* loadTimesheets(): SagaIterator {
    // Select params
    const params = yield select(selectLoadTimesheetsParams);

    yield call(axiosPoll, {
        config: {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params
        },
        succeeded: loadTimesheetsSucceeded,
        failed: loadTimesheetsFailed
    });
}

export function* watchTimesheetsLoadRequested(): SagaIterator {
    let lastTask;
    while (true) {
        const [requested] = yield race([
            take(TIMESHEETS_LOAD_REQUESTED),
            take(TIMESHEETS_LOAD_CANCELED)
        ]);
        if (lastTask) {
            yield cancel(lastTask);
        }
        if (requested) {
            lastTask = yield fork(loadTimesheets);
        }
    }
}

export function* approveTimesheet(
    action: RequestApproveTimesheetAction
): SagaIterator {
    const {
        payload: { timesheetId }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Timesheets/${timesheetId}/approve`;

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'post',
            url,
            headers: {
                Authorization: idToken
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetsParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetsSucceeded, getResponse);

        // Succeeded Approve Timesheet
        yield put(succeededApproveTimesheet(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet approved successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                timesheetApproveModal: undefined,
                selectedTimesheet: undefined
            })
        );
    } catch (error) {
        yield put(failedApproveTimesheet(error));
    }
}

export function* unapproveTimesheet(
    action: RequestUnapproveTimesheetAction
): SagaIterator {
    const {
        payload: { timesheetId }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    const url = `${REACT_APP_API_URL}/v1/Timesheets/${timesheetId}/approve`;

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'delete',
            url,
            headers: {
                Authorization: idToken
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetsParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetsSucceeded, getResponse);

        yield put(succeededUnapproveTimesheet(response));

        yield put(
            enqueueSnackbar({
                message: 'Timesheet unapproved successfully',
                options: { variant: 'success' }
            })
        );

        yield put(
            updateQueryString({
                [TIMESHEET_UNAPPROVE_MODAL]: undefined,
                selectedTimesheet: undefined
            })
        );
    } catch (error) {
        yield put(failedUnapproveTimesheet(error));
    }
}

function deleteTimesheetApi(
    timesheetId: number,
    token: string
): Promise<AxiosPromise> {
    const url = `${REACT_APP_API_URL}/v1/Timesheets/${timesheetId}`;

    return axios.delete(url, {
        headers: {
            Authorization: token
        }
    });
}

function getTimesheetsApi(
    params: Record<string, any>,
    token: string
): Promise<AxiosPromise> {
    const url = `${REACT_APP_API_URL}/v1/Timesheets`;

    return axios.get(url, {
        params,
        headers: {
            Authorization: token
        }
    });
}

export function* deleteTimesheet(
    action: RequestDeleteTimesheetAction
): SagaIterator {
    const {
        payload: { timesheetId }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    try {
        const response = yield call(deleteTimesheetApi, timesheetId, idToken);

        // Select params
        const params = yield select(selectLoadTimesheetsParams);

        const getResponse = yield call(getTimesheetsApi, params, idToken);

        yield call(loadTimesheetsSucceeded, getResponse);

        // Succeeded Delete Timesheet
        yield put(succeededDeleteTimesheet(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet delete successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                timesheetDeleteModal: undefined,
                selectedTimesheet: undefined
            })
        );
    } catch (error) {
        yield put(failedDeleteTimesheet(error));
    }
}

export function* watchTimesheetApproveRequested(): SagaIterator {
    yield takeEvery(TIMESHEET_APPROVE_REQUESTED, approveTimesheet);
}

export function* watchTimesheetUnapproveRequested(): SagaIterator {
    yield takeEvery(TIMESHEET_UNAPPROVE_REQUESTED, unapproveTimesheet);
}

export function* watchTimesheetDeleteRequested(): SagaIterator {
    yield takeEvery(TIMESHEET_DELETE_REQUESTED, deleteTimesheet);
}

export function* submitTimesheet(
    action: RequestSubmitTimesheetAction
): SagaIterator {
    const {
        payload: { timesheetId }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'post',
            url: `${REACT_APP_API_URL}/v1/Payroll/datacom`,
            headers: {
                Authorization: idToken
            },
            data: {
                timesheetIds: [timesheetId]
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetsParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetsSucceeded, getResponse);

        // Succeeded Submit Timesheet
        yield put(succeededSubmitTimesheet(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheet submitted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                timesheetSubmitModal: undefined,
                selectedTimesheet: undefined
            })
        );
    } catch (error) {
        yield put(failedSubmitTimesheet(error));
    }
}

export function* submitMultipleTimesheets(
    action: RequestSubmitMultipleTimesheetsAction
): SagaIterator {
    const {
        payload: { timesheetIds }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    try {
        // @ts-ignore
        const response = yield call(axios, {
            method: 'post',
            url: `${REACT_APP_API_URL}/v1/Payroll/datacom`,
            headers: {
                Authorization: idToken
            },
            data: {
                timesheetIds
            }
        });

        // Select params
        const params = yield select(selectLoadTimesheetsParams);

        // @ts-ignore
        const getResponse = yield call(axios, {
            method: 'GET',
            url: `${REACT_APP_API_URL}/v1/Timesheets`,
            params,
            headers: {
                Authorization: idToken
            }
        });

        yield call(loadTimesheetsSucceeded, getResponse);

        // Succeeded Submit Multiple Timesheets
        yield put(succeededSubmitMultipleTimesheets(response));

        yield put(resetSelectedTableCells());

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Timesheets submitted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                submitMultipleTimesheetsModal: undefined
            })
        );
    } catch (error) {
        yield put(failedSubmitMultipleTimesheets(error));
    }
}

export function* watchTimesheetSubmitMultipleRequested(): SagaIterator {
    yield takeEvery(
        TIMESHEET_MULTIPLE_SUBMIT_REQUESTED,
        submitMultipleTimesheets
    );
}

export function* watchTimesheetSubmitRequested(): SagaIterator {
    yield takeEvery(TIMESHEET_SUBMIT_REQUESTED, submitTimesheet);
}

export function* exportDatapayHolidayDays(): SagaIterator {
    const organizationId = yield select(selectCurrentOrganizationId);
    const startDate = yield select(selectFirstDate);
    const endDate = yield select(selectLastDate);
    const idToken = yield select(idTokenSelector);
    const url = `${REACT_APP_API_URL}/v1/Payroll/datacom/export/ldw`;

    try {
        const response: AxiosResponse = yield call(
            axios.post,
            url,
            {
                organizationId,
                startDate,
                endDate
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/csv',
                    Authorization: idToken
                }
            }
        );

        // Succeeded DataPay Export
        yield put(succeededExportDatapayHolidayDays(response));

        const time = moment.utc().format();

        // File Download
        fileDownload(
            response.data,
            `datacom_${startDate}_${endDate}_${time}.csv`
        );

        yield put(
            updateQueryString({
                exportTimesheetsModal: undefined
            })
        );
    } catch (error) {
        yield put(failedExportDatapayHolidayDays(error));
    }
}

export function* watchDatapayHolidayDaysRequested(): SagaIterator {
    yield takeEvery(
        DATAPAY_HOLIDAY_DAYS_EXPORT_REQUESTED,
        exportDatapayHolidayDays
    );
}

export default function* timesheetsSagas(): SagaIterator {
    yield all([
        fork(watchTimesheetsLoadRequested),
        fork(watchTimesheetApproveRequested),
        fork(watchTimesheetUnapproveRequested),
        fork(watchTimesheetDeleteRequested),
        fork(watchTimesheetSubmitRequested),
        fork(watchTimesheetSubmitMultipleRequested),
        fork(watchDatapayHolidayDaysRequested)
    ]);
}
